import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../utils/api';
import { api_url } from '../config';

const initialState = {
  patients: [],
  patientDetail: {},
  loading: false,
  error: null,
  sort: null,
  filters: {}
};

export const fetchPatients = createAsyncThunk(
  'patients/fetchPatients',
  async ({ userId, branchId }, { getState }) => {
    const { auth: { token } } = getState();
    console.log('Fetching patients for:', { userId, branchId });

    const response = await api.get(
      `${api_url}/api/v1/retailer/user/${userId}/branch/${branchId}/patient-list`, 
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      }
    );
    console.log('API Response:', response.data);
    return response.data;
  }
);

export const addPatient = createAsyncThunk(
  'patients/addPatient',
  async (patientData, { getState, rejectWithValue }) => {
    try {
      const { auth: { token, id, branch_id } } = getState();

      const response = await api.post(
        `${api_url}/api/v1/retailer/user/${id}/branch/${branch_id}/patient`,
        patientData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 
        error.message || 
        'Failed to add patient'
      );
    }
  }
);

export const fetchPatientList = createAsyncThunk(
  'patients/fetchPatientList',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth: { token, id, branch_id } } = getState();

      const response = await api.get(
        `${api_url}/api/v1/retailer/user/${id}/branch/${branch_id}/patient-list`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 
        error.message || 
        'Failed to fetch patient list'
      );
    }
  }
);

export const fetchPatient = createAsyncThunk(
  'prescriptions/fetchPatient',
  async ({ patientId }, { getState, rejectWithValue }) => {
    try {
      const { auth: { token, id, branch_id } } = getState();

      const response = await api.get(
        `${api_url}/api/v1/retailer/user/${id}/branch/${branch_id}/patient/${patientId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch patient'
      );
    }
  }
);

export const updatePatient = createAsyncThunk(
  'patients/updatePatient',
  async ({ patientId, patientData }, { getState, rejectWithValue }) => {
    try {
      const { auth: { token, id, branch_id } } = getState();

      const response = await api.put(
        `${api_url}/api/v1/retailer/user/${id}/branch/${branch_id}/patient/${patientId}`,
        patientData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 
        error.message || 
        'Failed to update patient'
      );
    }
  }
);

const patientsSlice = createSlice({
  name: 'patients',
  initialState,
  reducers: {
    setSorting: (state, action) => {
      state.sort = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {};
      state.sort = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPatients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatients.fulfilled, (state, action) => {
        state.loading = false;
        console.log('Setting patients in state:', action.payload);
        state.patients = action.payload.data;
      })
      .addCase(fetchPatients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchPatient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatient.fulfilled, (state, action) => {
        state.loading = false;
        state.patientDetail = action.payload.data;
      })
      .addCase(fetchPatient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchPatientList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatientList.fulfilled, (state, action) => {
        state.loading = false;
        state.patients = action.payload.data;
      })
      .addCase(fetchPatientList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(addPatient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addPatient.fulfilled, (state, action) => {
        state.loading = false;
        state.patients.unshift(action.payload);
        state.currentPatient = null;
      })
      .addCase(addPatient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updatePatient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePatient.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.patients.findIndex(
          p => p.id === action.payload.id
        );
        if (index !== -1) {
          state.patients[index] = action.payload;
        }
        state.currentPatient = null;
      })
      .addCase(updatePatient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setSorting, setFilters, clearFilters } = patientsSlice.actions;

export default patientsSlice.reducer;